import { Stack } from '@mui/material';
import { logger } from '../../services/logger/logger';
import { ColDef } from 'ag-grid-community';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import ClientSideAgGrid from '../../components/common/customAgGrid/ClientSideAgGrid';
import CustomActionButton from '../../components/common/customAgGrid/CustomActionButton';
import CustomAgMultiSelectFilter from '../../components/common/customAgGrid/CustomAgMultiSelectFilter';
import CustomAgTextFilter from '../../components/common/customAgGrid/CustomAgTextFilter';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import EditExtraCostModal from '../../components/extracosts/EditExtraCostModal';
import PageHeader from '../../components/PageHeader';
import { ExtraCostItem } from '../../store/apiSlices/extracosts/extraCostsApiParser';
import {
  useCreateExtraCostMutation,
  useDeleteExtraCostMutation,
  useGetExtraCostsListQuery,
  useUpdateExtraCostMutation,
} from '../../store/apiSlices/extracosts/extraCostsApis';
import { canEditReferenceData } from '../../utils/permissions';
import { showSuccessToast } from '../../components/common/BaseToaster';
import { TableCardContent } from '../../components/common/styled/TableCardContent';
import { useDeleteConfirmation } from '../../hooks/useDeleteConfirmation';
import { useTranslation } from 'react-i18next';

// ------------------- Types -------------------
interface ActionButton {
  label: string;
  variant?: 'blue' | 'outlinedBlue' | 'red' | 'gray' | 'text';
  onClick: () => void;
}

interface BreadcrumbOption {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

// ------------------- Component -------------------
const ExtraCostsContainer: React.FC = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const canAddorEdit = canEditReferenceData(userInfo?.authorityName);
  const [showAddExtraCostModal, setShowAddExtraCostModal] = useState<boolean>(false);
  const [editingExtraCost, setEditingExtraCost] = useState<ExtraCostItem | null>(null);
  type ExtraCostFormValues = {
    id: number;
    extraCostName: string;
    allocationType: string;
  };
  const [tableColumns, setTableColumns] = useState<ColDef[]>([]);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [resetFiltersSignal, setResetFiltersSignal] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ExtraCostFormValues>({
    defaultValues: {
      id: 0,
      extraCostName: '',
      allocationType: '',
    },
  });
  //Api call
  const {
    data: extraCostsList,
    isLoading,
    refetch,
  } = useGetExtraCostsListQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const [createExtraCost] = useCreateExtraCostMutation();
  const [updateExtraCost] = useUpdateExtraCostMutation();
  const [deleteExtraCost, { isLoading: isDeleting }] = useDeleteExtraCostMutation();

  const { showDeleteConfirm, handleConfirmDelete, onDeleteClick, onCloseDeleteConfirm } =
    useDeleteConfirmation<ExtraCostItem>({
      deleteMutation: (id) => deleteExtraCost({ id }) as any,
      refetch,
      itemName: 'extra cost',
      idField: 'id',
    });

  // Page header action buttons
  const actionBtns: ActionButton[] = canAddorEdit
    ? [
        {
          label: t('extraCosts.addExtraCost'),
          variant: 'blue',
          onClick: () => {
            reset({ id: 0, extraCostName: '', allocationType: '' });
            setShowAddExtraCostModal(true);
          },
        },
      ]
    : [];

  // Breadcrumb options
  const breadcrumbOptions: BreadcrumbOption[] = [
    { label: t('extraCosts.title'), isActive: true, onClick: () => {} },
  ];

  // Handle edit button click
  const handleEditClick = useCallback(
    (rowData: ExtraCostItem) => {
      setEditingExtraCost(rowData);

      let allocationType: string | undefined;
      switch (rowData.allocationType) {
        case 'By Price':
          allocationType = 'V';
          break;
        case 'By Quantity':
          allocationType = 'Q';
          break;
        case 'Non-FIFO':
          allocationType = 'N';
          break;
        default:
          allocationType = 'N';
      }

      reset({
        id: rowData.id,
        extraCostName: rowData.extraCostName,
        allocationType: allocationType,
      });
      setShowAddExtraCostModal(true);
    },
    [reset],
  );

  // Table columns
  const columns: ColDef[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: t('table.columns.id'),
        sortable: true,
        width: 120,
        maxWidth: 120,
        flex: 0,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgTextFilter,
      },
      {
        field: 'extraCostName',
        headerName: t('table.columns.extraCostName'),
        width: 200,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'allocationType',
        headerName: t('table.columns.type'),
        width: 150,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'actions',
        headerName: '',
        width: 50,
        maxWidth: 50,
        flex: 0,
        sortable: false,
        pinned: 'right',
        cellRenderer: (params: any) => (
          <CustomActionButton
            menuOptions={[
              {
                label: t('buttons.edit'),
                onClick: () => handleEditClick(params.data),
              },
              {
                label: t('buttons.delete'),
                onClick: () => onDeleteClick(params.data),
              },
            ]}
          />
        ),
      },
    ],
    [handleEditClick, onDeleteClick, t],
  );

  useEffect(() => {
    setTableColumns(canAddorEdit ? columns : columns.filter((col) => col.field !== 'actions'));
  }, [canAddorEdit, columns]);

  // Handle form submission
  const onSubmit = useCallback(
    async (values: ExtraCostFormValues) => {
      try {
        const extraCostName = values.extraCostName?.trim();
        const allocationType = values.allocationType?.trim();

        if (editingExtraCost) {
          const res: any = await updateExtraCost({
            id: values.id,
            updates: { id: values.id, name: extraCostName, allocationType },
          }).unwrap();
          if (res?.success) {
            refetch();
            showSuccessToast(res?.message);
          }
        } else {
          const res: any = await createExtraCost({ name: extraCostName, allocationType }).unwrap();
          if (res?.success) {
            refetch();
            showSuccessToast(res?.message);
          }
        }

        setShowAddExtraCostModal(false);
        setEditingExtraCost(null);
        reset({ id: 0, extraCostName: '', allocationType: '' });
      } catch (error) {
        logger.error('Failed to update/create extra cost', error as Error);
      }
    },
    [editingExtraCost, createExtraCost, updateExtraCost, refetch, reset],
  );

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setShowAddExtraCostModal(false);
    setEditingExtraCost(null);
    reset({ id: 0, extraCostName: '', allocationType: '' });
  }, [reset]);

  return (
    <Stack gap={1} sx={{ height: '100%', minHeight: 0 }}>
      <PageHeader
        actions={actionBtns}
        options={breadcrumbOptions}
        isFilterActive={isFilterActive}
        resetFilters={() => setResetFiltersSignal(true)}
      />
      <TableCardContent>
        <ClientSideAgGrid
          rowData={extraCostsList ?? []}
          columnDefs={tableColumns as any}
          loading={isLoading}
          className="extra-costs-grid"
          onFiltersActiveChange={(active: boolean) => {
            setIsFilterActive(active);
            setResetFiltersSignal(false);
          }}
          resetFiltersSignal={resetFiltersSignal}
        />
      </TableCardContent>

      {showDeleteConfirm && (
        <DeleteConfirmModal
          open={showDeleteConfirm}
          title={t('extraCosts.deleteExtraCost')}
          description={t('common.deleteConfirmation')}
          isSubmitting={isDeleting}
          onClose={onCloseDeleteConfirm}
          onConfirm={handleConfirmDelete}
        />
      )}

      {showAddExtraCostModal && (
        <EditExtraCostModal
          onClose={handleCloseModal}
          onSubmit={handleSubmit(onSubmit)}
          control={control}
          errors={errors}
          isEditMode={!!editingExtraCost}
        />
      )}
    </Stack>
  );
};

export default ExtraCostsContainer;
